import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    FormControl, InputLabel, Select, MenuItem, Box, Typography
} from '@mui/material';
import { SubscriptionBillingService } from '../../services/subscriptionBilling.service';
import { useToast } from '../../context/ToastContext';
import { IInstitution } from '../../interfaces/institution.interface';

interface IInstitutionWithEmployeeCount extends IInstitution {
    employeeCount?: number;
}

interface Props {
    open: boolean;
    institutions: IInstitutionWithEmployeeCount[];
    onClose: () => void;
    onSuccess: () => void;
}

const InvoiceCreateDialog: React.FC<Props> = ({ open, institutions, onClose, onSuccess }) => {
    const { showSuccess, showApiError } = useToast();
    const [submitting, setSubmitting] = useState(false);

    const [institutionId, setInstitutionId] = useState('');
    const [periodStart, setPeriodStart] = useState('');
    const [periodEnd, setPeriodEnd] = useState('');
    const [amount, setAmount] = useState('');
    const [description, setDescription] = useState('');
    const [dueDate, setDueDate] = useState('');

    useEffect(() => {
        if (open) {
            setInstitutionId('');
            setPeriodStart('');
            setPeriodEnd('');
            setAmount('');
            setDescription('');
            setDueDate('');
        }
    }, [open]);

    const selectedInstitution = institutions.find(inst => inst.id === institutionId);

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            await SubscriptionBillingService.createInvoice({
                institutionId,
                periodStart,
                periodEnd,
                amount: Number(amount),
                description: description || undefined,
                dueDate: dueDate || undefined,
                employeeCount: selectedInstitution?.employeeCount,
            });
            showSuccess('Invoice created');
            onSuccess();
            onClose();
        } catch (error) {
            showApiError(error, 'Failed to create invoice');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Create Invoice</DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <FormControl fullWidth required>
                        <InputLabel id="invoice-institution-label">Institution</InputLabel>
                        <Select
                            labelId="invoice-institution-label"
                            label="Institution"
                            value={institutionId}
                            onChange={(e) => setInstitutionId(e.target.value)}
                            inputProps={{ 'aria-label': 'Institution' }}
                        >
                            {institutions.map(inst => (
                                <MenuItem key={inst.id} value={inst.id}>{inst.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {selectedInstitution && (
                        <Typography variant="body2" color="text.secondary">
                            Employees: {selectedInstitution.employeeCount ?? 'N/A'}
                        </Typography>
                    )}

                    <TextField
                        label="Period Start"
                        type="date"
                        value={periodStart}
                        onChange={(e) => setPeriodStart(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        required
                        fullWidth
                    />

                    <TextField
                        label="Period End"
                        type="date"
                        value={periodEnd}
                        onChange={(e) => setPeriodEnd(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        required
                        fullWidth
                    />

                    <TextField
                        label="Amount"
                        type="text"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        inputProps={{ inputMode: 'decimal' }}
                        required
                        fullWidth
                    />

                    <TextField
                        label="Description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        multiline
                        rows={2}
                        fullWidth
                    />

                    <TextField
                        label="Due Date"
                        type="date"
                        value={dueDate}
                        onChange={(e) => setDueDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>Cancel</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={submitting || !institutionId || !periodStart || !periodEnd || !amount}
                >
                    {submitting ? 'Creating…' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default InvoiceCreateDialog;
